"use client"

import type React from "react"

import { useState, useRef } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Camera, Upload, ArrowLeft, Check } from "lucide-react"
import Link from "next/link"

export default function CameraPage() {
  const router = useRouter()
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [capturedFile, setCapturedFile] = useState<File | null>(null)
  const [isPublic, setIsPublic] = useState<boolean>(true)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)

  const handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCapturedFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setCapturedImage(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpload = async () => {
    if (!capturedImage || !capturedFile) return

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append("photo", capturedFile)
      formData.append("isPublic", isPublic.toString())

      const response = await fetch("/api/photos", {
        method: "POST",
        body: formData,
      })

      if (response.ok) {
        setUploadSuccess(true)
        setTimeout(() => {
          if (isPublic) {
            router.push("/gallery")
          } else {
            router.push("/")
          }
        }, 2000)
      } else {
        alert("Erro ao fazer upload da foto. Tente novamente.")
      }
    } catch (error) {
      console.error("Upload error:", error)
      alert("Erro ao fazer upload da foto. Tente novamente.")
    } finally {
      setIsUploading(false)
    }
  }

  const handleRetake = () => {
    setCapturedImage(null)
    setCapturedFile(null)
    setUploadSuccess(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    if (galleryInputRef.current) {
      galleryInputRef.current.value = ""
    }
  }

  return (
    <main className="flex min-h-screen flex-col bg-secondary/30">
      {/* Header */}
      <div className="w-full bg-card border-b border-border p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="font-bold text-xl text-foreground">Foto com o Janota</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-6">
          {!capturedImage ? (
            <Card className="p-8 space-y-6">
              <div className="text-center space-y-3">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <Camera className="h-10 w-10 text-primary" />
                </div>
                <h2 className="font-semibold text-2xl text-foreground">Tira a tua foto</h2>
                <p className="text-muted-foreground">Tira uma foto com a câmara ou escolhe uma da galeria</p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleCapture}
                className="hidden"
                id="camera-input"
              />

              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={handleCapture}
                className="hidden"
                id="gallery-input"
              />

              <div className="space-y-3">
                <label htmlFor="camera-input" className="block">
                  <Button size="lg" className="w-full h-14 text-lg font-semibold" type="button" asChild>
                    <span>
                      <Camera className="mr-2 h-5 w-5" />
                      Abrir câmara
                    </span>
                  </Button>
                </label>

                <label htmlFor="gallery-input" className="block">
                  <Button size="lg" variant="outline" className="w-full h-14 text-lg font-semibold bg-transparent" type="button" asChild>
                    <span>
                      <Upload className="mr-2 h-5 w-5" />
                      Escolher da galeria
                    </span>
                  </Button>
                </label>
              </div>
            </Card>
          ) : (
            <Card className="p-6 space-y-6">
              {/* Preview */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">A tua foto com o Janota</h3>
                <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <img
                    src={capturedImage || "/placeholder.svg"}
                    alt="Captured"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Privacy Selection */}
              {!uploadSuccess && (
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Visibilidade da foto</Label>
                  <RadioGroup
                    value={isPublic ? "public" : "private"}
                    onValueChange={(val) => setIsPublic(val === "public")}
                  >
                    <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                      <RadioGroupItem value="public" id="public" />
                      <Label htmlFor="public" className="flex-1 cursor-pointer font-normal">
                        <div className="font-semibold">Pública</div>
                        <div className="text-sm text-muted-foreground">A tua foto aparecerá na galeria pública</div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors">
                      <RadioGroupItem value="private" id="private" />
                      <Label htmlFor="private" className="flex-1 cursor-pointer font-normal">
                        <div className="font-semibold">Privada</div>
                        <div className="text-sm text-muted-foreground">A tua foto ficará visível apenas para os donos do Janota</div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Success Message */}
              {uploadSuccess && (
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Foto enviada com sucesso!</p>
                    <p className="text-sm text-muted-foreground">A redirecionar...</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {!uploadSuccess && (
                <div className="flex gap-3">
                  <Button variant="outline" size="lg" onClick={handleRetake} className="flex-1 bg-transparent">
                    Tirar outra
                  </Button>
                  <Button size="lg" onClick={handleUpload} disabled={isUploading} className="flex-1">
                    <Upload className="mr-2 h-5 w-5" />
                    {isUploading ? "Enviando..." : "Enviar foto"}
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </main>
  )
}
